import { useMemo, useRef, useState, type WheelEvent } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import type { InterviewerOption } from '../../libs/api/interviewService';

interface InterviewerComboboxProps {
  interviewers: InterviewerOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
}

const normalizeSearchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .trim();

export default function InterviewerCombobox({
  interviewers,
  value,
  onChange,
  placeholder = 'Select interviewer',
  className,
}: InterviewerComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const selected = interviewers.filter((item) => value.includes(item.id));
  const filteredInterviewers = useMemo(() => {
    const normalizedQuery = normalizeSearchText(query);

    if (!normalizedQuery) return interviewers;

    return interviewers.filter((item) =>
      normalizeSearchText(`${item.display_name} ${item.email}`).includes(normalizedQuery),
    );
  }, [interviewers, query]);

  const handleWheel = (event: WheelEvent) => {
    const list = listRef.current;
    if (!list) return;

    const canScroll = list.scrollHeight > list.clientHeight;
    if (!canScroll) return;

    event.preventDefault();
    event.stopPropagation();
    list.scrollTop += event.deltaY;
  };

  const toggleInterviewer = (id: string) => {
    onChange(value.includes(id) ? value.filter((item) => item !== id) : [...value, id]);
  };

  const removeInterviewer = (id: string) => {
    onChange(value.filter((item) => item !== id));
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`min-h-9 h-auto w-full justify-between px-3 py-1.5 font-normal ${className ?? ''}`}
        >
          <span className="flex min-w-0 flex-1 flex-wrap gap-1">
            {selected.length > 0 ? (
              selected.map((item) => (
                <span
                  key={item.id}
                  className="inline-flex max-w-full items-center gap-1 rounded border border-primary/20 bg-primary-light px-1.5 py-0.5 text-xs font-semibold text-primary"
                >
                  <span className="truncate">{item.display_name}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    className="rounded text-primary/70 hover:text-primary"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      removeInterviewer(item.id);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        removeInterviewer(item.id);
                      }
                    }}
                    aria-label={`Remove ${item.display_name}`}
                  >
                    <X size={11} />
                  </span>
                </span>
              ))
            ) : (
              <span className="truncate text-text-subtle">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 text-text-subtle" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] overflow-hidden p-0"
        align="start"
        sideOffset={4}
        onWheelCapture={handleWheel}
      >
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Search interviewer..."
          />
          <CommandList
            ref={listRef}
            className="h-64 max-h-64 overflow-y-scroll overscroll-contain [scrollbar-gutter:stable]"
            onWheel={handleWheel}
          >
            {filteredInterviewers.length === 0 ? (
              <CommandEmpty>No interviewer found.</CommandEmpty>
            ) : null}
            <CommandGroup>
              {filteredInterviewers.map((item) => (
                <CommandItem
                  key={item.id}
                  value={item.id}
                  onSelect={() => {
                    toggleInterviewer(item.id);
                    setQuery('');
                  }}
                >
                  <Check
                    size={14}
                    className={value.includes(item.id) ? 'opacity-100' : 'opacity-0'}
                  />
                  <span className="truncate">{item.display_name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
